import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { jsonHash } from 'utils/json-hash';

const reactPlugin = new ReactPlugin();
let appInsights: ApplicationInsights;
const GOOGLE_ANALYTICS = 'google-analytics';

/**
 * Create the App Insights Telemetry Service
 * @return {{reactPlugin: ReactPlugin, appInsights: Object, initialize: Function}} - Object
 */
const isTelemetryServiceEventSourceFound = (context: string, event: string) => context.indexOf(event) > -1;

const createTelemetryService = () => {
  /**
   * Initialize the Application Insights class
   * @param {string} instrumentationKey - Application Insights Instrumentation Key
   * @param {Object} browserHistory - client's browser history, supplied by the withRouter HOC
   * @return {void}
   */
  const initialize = (instrumentationKey: string, browserHistory: History) => {
    if (!browserHistory) {
      throw new Error('Could not initialize Telemetry Service');
    }

    if (!instrumentationKey) {
      throw new Error('Instrumentation key not provided');
    }

    appInsights = new ApplicationInsights({
      config: {
        instrumentationKey,
        disableDataLossAnalysis: false,
        disableFetchTracking: false,
        disableCorrelationHeaders: false,
        correlationHeaderDomains: [
          new URL(window.apiOriginUrl, window.location.origin).toString(),
          window.location.origin,
        ],
        enableCorsCorrelation: true,
        /**
         * @see https://github.com/microsoft/applicationinsights-react-js/issues/32
         */
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: {
            history: browserHistory,
          },
        },
      },
    });

    appInsights.loadAppInsights();

    const telemetryInitializer = (context: ITelemetryItem) => {
      if (context.data) {
        context.data.ScreenSize = `${window.screen.width}X${window.screen.height}`;
      }

      if (context.baseType === 'ExceptionData') {
        // pick some unique data related to exception description
        // data in `context` may be different on different stages/AI versions

        const uniqueIssueId = {
          url: context.baseData?.url || context.ext?.trace?.name,
          messages: context.baseData?.exceptions?.map((x: Error) => x.message),
          callStack: context.baseData?.exceptions?.map((x: Error) => x.stack),
          errorObj: String(context.baseData?.error),
          message: context.baseData?.message,
        };

        (context.data ?? {}).uniqueIssueIdHash = jsonHash(uniqueIssueId, 'sha1').slice(-10);
      }

      if (context.baseType === 'RemoteDependencyData') {
        const isGoogleAnalyticsEventSource =
          isTelemetryServiceEventSourceFound(context.baseData?.name, GOOGLE_ANALYTICS) ||
          isTelemetryServiceEventSourceFound(context.baseData?.target, GOOGLE_ANALYTICS);

        if (isGoogleAnalyticsEventSource) {
          return false;
        }
      }
    };

    appInsights.addTelemetryInitializer(telemetryInitializer);
  };

  return { reactPlugin, appInsights, initialize };
};

export const ai = createTelemetryService();
export const getAppInsights = () => appInsights;
